import gsap from "gsap";
import { fillModal } from "./content";
import { smoothOn, smoothOff } from "./smooth";
import { chime, tone, swish } from "./sound";
import type {
  SpiralItem,
  SpiralApi,
  MenuApi,
  PagesApi,
  ModalApi,
  ViewToggleApi,
  HoverLabelApi,
} from "./types";

const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
const fine    = window.matchMedia("(hover: hover) and (pointer: fine)").matches;

const $  = (selector: string, root: ParentNode = document) => root.querySelector<HTMLElement>(selector);
const $$ = (selector: string, root: ParentNode = document) => Array.from(root.querySelectorAll<HTMLElement>(selector));

// Theme colours used in the menu morph animation.
const DARK  = "#0b0c10";
const CREAM = "#f1ede4";

// Shared reference to the spiral, set after it's created.
let spiralRef: SpiralApi | null = null;

export function setSpiral(s: SpiralApi | null): void {
  spiralRef = s;
}

// ── Smooth scroll binding ─────────────────────────────────────────────────────
// Lenis is bound to whichever overlay is on top. Only one overlay scrolls at a time.

function applySmooth(): void {
  const modal = $("#modal");
  if (modal?.classList.contains("is-open")) {
    return smoothOn($(".modal__inner"), $(".modal__content"));
  }

  const openPage = $$(".page").find((p) => p.classList.contains("is-on"));
  if (openPage) {
    return smoothOn(openPage.querySelector(".page__inner"), openPage.querySelector(".page__content"));
  }

  const listView = $("#listView");
  if (listView?.classList.contains("is-on")) {
    return smoothOn(listView, $("#listWrap"));
  }

  smoothOff();
}

// Re-applies the spiral dim, cursor overlay class, and top-bar state whenever
// any overlay opens or closes.
function refreshOverlay(): void {
  const isAnyOpen =
    $("#menu")?.classList.contains("is-open") ||
    $("#modal")?.classList.contains("is-open") ||
    $("#listView")?.classList.contains("is-on") ||
    $$(".page").some((p) => p.classList.contains("is-on"));

  document.body.classList.toggle("overlay-on", !!isAnyOpen);

  // Minimise the top bar over the menu and full-screen pages (but NOT over the
  // list view, which still needs the spiral/list toggle visible).
  const isPageOn = $$(".page").some((p) => p.classList.contains("is-on"));
  const isBarMin = $("#menu")?.classList.contains("is-open") || isPageOn;
  document.body.classList.toggle("bar-min", !!isBarMin);
  document.body.classList.toggle("page-on", isPageOn);

  spiralRef?.setDim(isAnyOpen ? 1 : 0);
  applySmooth();
}

// ── Cursor ────────────────────────────────────────────────────────────────────

export function initCursor(): void {
  if (!fine) return;
  document.body.classList.add("has-cursor");

  const cursor = $(".cursor")!;
  const dot  = cursor.querySelector<HTMLElement>(".cursor__dot")!;
  const ring = cursor.querySelector<HTMLElement>(".cursor__ring")!;

  let mouseX = 0, mouseY = 0;
  let ringCurX = 0, ringCurY = 0;
  let dotScale = 1, dotScaleTarget = 1;

  const RING_LERP  = 0.16;
  const SCALE_LERP = 0.18;

  window.addEventListener("pointermove", (e) => {
    mouseX = e.clientX;
    mouseY = e.clientY;
  }, { passive: true });

  document.addEventListener("pointerover", (e) => {
    if ((e.target as Element).closest("[data-cursor='view'], button, a")) {
      cursor.classList.add("is-view");
      dotScaleTarget = 1.7;
    }
  });
  document.addEventListener("pointerout", (e) => {
    if ((e.target as Element).closest("[data-cursor='view'], button, a")) {
      cursor.classList.remove("is-view");
      dotScaleTarget = 1;
    }
  });

  (function animateCursor() {
    dotScale += (dotScaleTarget - dotScale) * SCALE_LERP;
    dot.style.transform = `translate(${mouseX}px, ${mouseY}px) scale(${dotScale.toFixed(3)})`;

    ringCurX += (mouseX - ringCurX) * RING_LERP;
    ringCurY += (mouseY - ringCurY) * RING_LERP;
    ring.style.transform = `translate(${ringCurX.toFixed(2)}px, ${ringCurY.toFixed(2)}px)`;

    requestAnimationFrame(animateCursor);
  })();
}

// ── Magnetic buttons ──────────────────────────────────────────────────────────

export function initMagnetic(): void {
  if (!fine || reduced) return;
  $$("[data-magnetic]").forEach((el) => {
    const xTo = gsap.quickTo(el, "x", { duration: 0.4, ease: "power3.out" });
    const yTo = gsap.quickTo(el, "y", { duration: 0.4, ease: "power3.out" });
    el.addEventListener("pointermove", (e) => {
      const rect = el.getBoundingClientRect();
      xTo((e.clientX - (rect.left + rect.width  / 2)) * 0.25);
      yTo((e.clientY - (rect.top  + rect.height / 2)) * 0.25);
    });
    el.addEventListener("pointerleave", () => { xTo(0); yTo(0); });
  });
}

// ── Sydney clock ──────────────────────────────────────────────────────────────

export function initClock(): void {
  const clockEls = $$("[data-clock]");
  const tick = () => {
    const time = new Date().toLocaleTimeString("en-AU", {
      timeZone: "Australia/Sydney",
      hour12: false,
    });
    clockEls.forEach((el) => { el.textContent = time; });
  };
  tick();
  setInterval(tick, 1000);
}

// ── Loader + hero intro sequence ──────────────────────────────────────────────

export function runLoaderIntro(readyPromise: Promise<string>, spiral: SpiralApi | null): void {
  const loader   = $(".loader");
  const countEl  = $("[data-loader-count]");
  const statusEl = $("[data-loader-status]");
  const heroLines = $$("[data-hero-line]");
  const fadeEls   = $$("[data-hero-fade], [nav-anim]");

  function intro(): void {
    if (reduced) {
      loader?.remove();
      if (spiral) spiral.tween.reveal = 1;
      return;
    }
    gsap.set(heroLines, { yPercent: 115 });
    gsap.set(fadeEls,   { autoAlpha: 0, y: 14 });

    const tl = gsap.timeline();
    tl.to(loader, { yPercent: -100, duration: 0.9, ease: "power4.inOut", onComplete: () => loader?.remove() })
      .to(heroLines, { yPercent: 0, duration: 1.2, stagger: 0.12, ease: "power4.out" }, "-=0.4")
      .to(fadeEls,   { autoAlpha: 1, y: 0, duration: 0.8, stagger: 0.06, ease: "power3.out" }, "-=0.8");

    if (spiral) tl.to(spiral.tween, { reveal: 1, duration: 2.6, ease: "power2.out" }, "-=1.5");
  }

  if (reduced) { readyPromise.then(intro); return; }

  const progress = { v: 0 };
  const render = () => {
    if (countEl) countEl.textContent = String(Math.round(progress.v)).padStart(3, "0");
  };

  const creep = gsap.to(progress, { v: 90, duration: 2.4, ease: "power1.inOut", onUpdate: render });

  const timeout = new Promise<string>((r) => setTimeout(() => r("timeout"), 7000));
  Promise.race([readyPromise, timeout]).then((result) => {
    creep.kill();
    if (statusEl) statusEl.textContent = result === "timeout" ? "running light engine" : "all aboard";
    gsap.to(progress, { v: 100, duration: 0.5, ease: "power2.out", onUpdate: render, onComplete: intro });
  });
}

// ── menu motion ──────────────────────────────────────────────────
// lowk this fuck ass animation broke a million times on desktop

export function initMenu(): MenuApi {
  const bar    = $("[data-bar]");
  const menu   = $("#menu");
  const panel  = $(".menu__panel");
  const body   = $(".menu__body");
  const openBtns = $$("[data-menu-open]");

  if (!menu || !openBtns.length) return { open() {}, close() {} };

  let openTL: gsap.core.Timeline | null = null;
  let closeTL: gsap.core.Timeline | null = null;
  let activeBtn = openBtns[0]; // the button that triggered the current open

  const RADIUS = 22; // menu border radius

  function panelFinalSize(btnRect: DOMRect): { width: number; height: number } {
    const margin = 12;
    const isMobile = window.innerWidth <= 640;
    let width = Math.max(380, Math.min(window.innerWidth * 0.42, 560));
    width = Math.min(width, btnRect.right - margin);
    if (isMobile) width = btnRect.right - margin;
    const bottomGap = isMobile ? Math.max(104, window.innerHeight * 0.17) : margin;
    const height = window.innerHeight - btnRect.top - bottomGap;
    return { width, height };
  }

  const open = (): void => {
    if (menu.classList.contains("is-open")) return;
    closeTL?.kill();
    chime({ open: true });

    menu.classList.add("is-open");
    menu.setAttribute("aria-hidden", "false");
    bar?.setAttribute("data-open", "");
    activeBtn.setAttribute("aria-expanded", "true");
    activeBtn.style.visibility = "hidden";
    gsap.set(activeBtn, { clearProps: "opacity" });
    refreshOverlay();

    const btnRect = activeBtn.getBoundingClientRect();
    const right   = window.innerWidth - btnRect.right; // pin right edge
    const finalSz = panelFinalSize(btnRect);

    if (reduced) {
      gsap.set(panel, { position: "fixed", right, top: btnRect.top, left: "auto", opacity: 1, ...finalSz, backgroundColor: CREAM, borderRadius: RADIUS });
      gsap.set(body, { opacity: 1 });
      return;
    }

    gsap.killTweensOf([panel, body]);
    gsap.set(panel, {
      position: "fixed", right, top: btnRect.top, left: "auto",
      width: btnRect.width, height: btnRect.height,
      minWidth: 0, borderRadius: RADIUS,
      backgroundColor: DARK, opacity: 1, overflow: "hidden",
    });
    gsap.set(body, { opacity: 0 });

    openTL = gsap.timeline();
    openTL
      .to(panel, { width: finalSz.width, duration: 0.34, ease: "power4.out" }, 0)
      .to(panel, { height: finalSz.height, duration: 0.42, ease: "power3.inOut" }, 0.09)
      .to(panel, { backgroundColor: CREAM, duration: 0.36, ease: "power2.inOut" }, 0.04)
      .to(body,  { opacity: 1, duration: 0.26, ease: "power2.out" }, 0.30)
      .fromTo(
        Array.from(body!.children) as HTMLElement[],
        { autoAlpha: 0, y: 18 },
        { autoAlpha: 1, y: 0, duration: 0.39, stagger: 0.04, ease: "power3.out" },
        0.34
      );
  };

  const close = (): void => {
    if (!menu.classList.contains("is-open")) return;
    openTL?.kill();
    chime({ open: false });

    bar?.removeAttribute("data-open");
    activeBtn.setAttribute("aria-expanded", "false");
    menu.classList.remove("is-open");
    menu.setAttribute("aria-hidden", "true");
    refreshOverlay();

    if (reduced) {
      gsap.set(panel, { opacity: 0 });
      activeBtn.style.visibility = "";
      return;
    }

    const btnRect = activeBtn.getBoundingClientRect();
    gsap.killTweensOf([panel, body, activeBtn]);

    // show real pill behind the panel so the crossfade hands off cleanly.
    activeBtn.style.visibility = "visible";
    gsap.set(activeBtn, { opacity: 0 });

    closeTL = gsap.timeline({
      onComplete: () => {
        gsap.set(panel, { opacity: 0 });
        activeBtn.style.visibility = "";
        gsap.set(activeBtn, { clearProps: "opacity" });
      },
    });
    // collapse back to pill then crossfade to the real menu button
    // FAH
    closeTL
      .to(body,  { opacity: 0, duration: 0.10, ease: "power2.in" }, 0)
      .to(panel, { height: btnRect.height, duration: 0.26, ease: "power3.inOut" }, 0.03)
      .to(panel, { width: btnRect.width, duration: 0.30, ease: "power4.inOut" }, 0.13)
      .to(panel, { backgroundColor: DARK, duration: 0.20, ease: "power2.inOut" }, 0.27)
      .to(panel, { opacity: 0, duration: 0.15, ease: "power2.inOut" }, 0.43)
      .to(activeBtn, { opacity: 1, duration: 0.15, ease: "power2.inOut" }, 0.43);
  };

  openBtns.forEach((btn) => {
    btn.addEventListener("click", () => {
      if (menu.classList.contains("is-open")) return close();
      activeBtn = btn;
      open();
    });
  });

  $$("[data-menu-close]", menu).forEach((el) => el.addEventListener("click", close));

  window.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && menu.classList.contains("is-open")) close();
  });

  return { open, close };
}

// ── full-screen pages motion ────────────────────────────────────────
export function initPages(menu: MenuApi | null): PagesApi {
  let aboutRevealed = false;
  let sponsorsRevealed = false;

  const openPage = (name: string): void => {
    const pageName = name[0].toUpperCase() + name.slice(1);
    const page = $(`#page${pageName}`);
    if (!page) return;

    menu?.close();

    // close other open pages silently
    $$(".page.is-on").forEach((p) => {
      if (p !== page) {
        p.classList.remove("is-on");
        p.setAttribute("aria-hidden", "true");
      }
    });

    page.classList.add("is-on");
    page.setAttribute("aria-hidden", "false");
    refreshOverlay();

    const scrollEl = page.querySelector<HTMLElement>(".page__inner");
    if (scrollEl) scrollEl.scrollTop = 0;

    if (!reduced) {
      gsap.fromTo(
        page.querySelectorAll(".page__content > *"),
        { autoAlpha: 0, y: 26 },
        { autoAlpha: 1, y: 0, duration: 0.7, stagger: 0.07, ease: "power3.out", delay: 0.18 }
      );
    }

    if (name === "about" && !aboutRevealed) {
      aboutRevealed = true;
      revealAbout();
    }
    if (name === "sponsors" && !sponsorsRevealed) {
      sponsorsRevealed = true;
      revealSponsors();
    }
  };

  const closePage = (page: Element): void => {
    if (!page.classList.contains("is-on")) return;
    chime({ open: false });
    page.classList.remove("is-on");
    page.setAttribute("aria-hidden", "true");
    refreshOverlay();
  };

  $$("[data-page-open]").forEach((btn) => {
    btn.addEventListener("click", () => openPage(btn.dataset["pageOpen"]!));
  });
  $$("[data-page-close]").forEach((btn) => {
    btn.addEventListener("click", () => closePage(btn.closest(".page")!));
  });
  window.addEventListener("keydown", (e) => {
    if (e.key === "Escape") $$(".page.is-on").forEach(closePage);
  });

  return { openPage, closePage };
}

// animates stats counter / word-fade
function revealAbout(): void {
  if (reduced) return;

  const ledeEl = $("#aboutLede");
  if (ledeEl?.textContent?.trim()) {
    const words = ledeEl.textContent.trim().split(/\s+/);
    // haha big number go up
    ledeEl.innerHTML = words.map((w) => `<span class="w">${w}</span>`).join(" ");
    gsap.to(ledeEl.querySelectorAll(".w"), {
      opacity: 1, stagger: 0.025, ease: "none", duration: 0.5, delay: 0.35,
    });
  }

  $$("#aboutStats [data-count]").forEach((el) => {
    const end = parseInt(el.dataset["count"]!, 10);
    const suffix = el.dataset["suffix"] ?? "";
    if (Number.isNaN(end)) return;

    const counter = { v: 0 };
    gsap.to(counter, {
      v: end,
      duration: 1.6,
      ease: "power2.out",
      delay: 0.4,
      onUpdate: () => {
        el.innerHTML = String(Math.round(counter.v)) + (suffix ? `<em>${suffix}</em>` : "");
      },
    });
  });
}

// staggers the sponsor cards in
function revealSponsors(): void {
  if (reduced) return;
  gsap.fromTo(
    "#sponsorsGrid .sponsor",
    { autoAlpha: 0, y: 34, scale: 0.97 },
    { autoAlpha: 1, y: 0, scale: 1, duration: 0.7, stagger: 0.08, ease: "power3.out", delay: 0.3 }
  );
}

// ── spiral / list toggle ─────────────────────────────────────────────────
export function initViewToggle(modal: ModalApi | null): ViewToggleApi {
  const listView = $("#listView");
  const toggleBtns = $$("[data-view]");

  const set = (view: "spiral" | "list"): void => {
    toggleBtns.forEach((btn) => {
      btn.classList.toggle("is-active", btn.dataset["view"] === view);
    });
    const isList = view === "list";
    listView?.classList.toggle("is-on", isList);
    listView?.setAttribute("aria-hidden", String(!isList));
    refreshOverlay();
  };

  toggleBtns.forEach((btn) => {
    btn.addEventListener("click", () => {
      swish({ up: btn.dataset["view"] === "spiral" });
      set(btn.dataset["view"] as "spiral" | "list");
    });
  });

  $$("#listList [data-list-i]").forEach((li) => {
    li.querySelector("a")?.addEventListener("click", (e) => {
      e.preventDefault();
      modal?.open(parseInt(li.dataset["listI"]!, 10));
    });
  });

  return { set };
}

// ── detail modal for spiral element ──────────────────────────────────
export function initModal(items: SpiralItem[]): ModalApi {
  const modal = $("#modal");
  if (!modal) return { open() {}, close() {} };

  const open = (i: number): void => {
    const item = items[i];
    if (!item) return;
    tone({ open: true });
    fillModal(item);
    modal.classList.add("is-open");
    modal.setAttribute("aria-hidden", "false");
    refreshOverlay();

    const scrollEl = $(".modal__inner");
    if (scrollEl) scrollEl.scrollTop = 0;

    // ease
    if (!reduced) {
      gsap.fromTo("#modalTitle",
        { yPercent: 38, autoAlpha: 0 },
        { yPercent: 0, autoAlpha: 1, duration: 0.7, ease: "power4.out", delay: 0.12 }
      );
      gsap.fromTo("#modalMedia",
        { autoAlpha: 0, y: 30 },
        { autoAlpha: 1, y: 0, duration: 0.7, ease: "power3.out", delay: 0.2 }
      );
      gsap.fromTo("#modalDesc, .modal__meta",
        { autoAlpha: 0, y: 16 },
        { autoAlpha: 1, y: 0, duration: 0.6, stagger: 0.06, ease: "power3.out", delay: 0.28 }
      );
    }
  };

  const close = (): void => {
    if (!modal.classList.contains("is-open")) return;
    tone({ open: false });
    modal.classList.remove("is-open");
    modal.setAttribute("aria-hidden", "true");
    refreshOverlay();
  };

  $$("[data-modal-close]", modal).forEach((btn) => btn.addEventListener("click", close));
  window.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && modal.classList.contains("is-open")) close();
  });

  return { open, close };
}

// ── callout ──────────────────
export function initHoverLabel(): HoverLabelApi {
  const label = $("#hoverLabel");
  if (!label) return { set(){} };

  const titleEl = label.querySelector<HTMLElement>(".hoverlabel__title")!;
  const yearEl = label.querySelector<HTMLElement>(".hoverlabel__year")!;
  let active = false;

  // you move i move ahh
  window.addEventListener("pointermove", (e) => {
    if (!active) return;
    label.style.left = `${e.clientX}px`;
    label.style.top  = `${e.clientY}px`;
  }, { passive: true });

  return {
    set(item: SpiralItem | null) {
      active = !!item;
      label.classList.toggle("is-on", active);
      if (item) {
        titleEl.textContent = item.title ?? "";
        yearEl.textContent  = item.year  ?? "";
      }
    },
  };
}
